/**
 * Documentation shown behind the info icons on the Resource Flow dashboard.
 *
 * The dashboard renders a single pair of charts driven by one set of metric
 * controls, so one text serves both the dashboard header and the flow chart;
 * the KPI tile carries its own. Moved verbatim out of
 * `MopaResourceFlowComponent`.
 */
export const MopaResourceFlowChartDocs = {
    dashboard: `<h2>Definition</h2>
<p><strong>Resource Flow</strong> compares inflow &#9650; and outflow &#9660; for one selected currency. It shows the selected metric over time, the resulting net balance, and a per-source breakdown.</p>

<h2>Metric controls</h2>
<ul>
  <li><strong>Value mode</strong> chooses Total, Average, Median / Percentile, Flow Records or Players.</li>
  <li><strong>User type</strong> restricts flow and denominators to one engagement population. See <em>User type</em> below.</li>
  <li><strong>Population</strong> applies only to Average and Median / Percentile. <em>Per DEU</em> (the default) counts players with a positive flow on the relevant side; <em>per DAU</em> uses the same population from <code>dashboard_users_activity</code> for both sides.</li>
  <li><strong>Percentiles</strong> apply only to Median / Percentile. Select P50, P75, P90 and/or P99; each selection produces its own inflow and outflow series. Flow is first summed per player and date bucket, then <code>APPROX_QUANTILES</code> is calculated over those player values. For <em>per DAU</em>, a player without flow contributes 0.</li>
</ul>

<p>All formulas below use inflow as the example; outflow is calculated the same way.</p>

<h2>Calculation</h2>
<table border="1" cellspacing="0" cellpadding="6">
  <thead>
    <tr bgcolor="#f5f5f5">
      <th align="left">Mode</th>
      <th align="left">Calculation</th>
      <th align="left">Denominator</th>
      <th align="left">Applied-range KPI</th>
    </tr>
  </thead>
  <tbody>
    <tr valign="top">
      <td><strong>Total</strong></td>
      <td><code>SUM(inflow)</code></td>
      <td>None &mdash; an absolute amount, not a ratio.</td>
      <td>Sum of daily values in the applied date range.</td>
    </tr>
    <tr valign="top">
      <td><strong>Flow Records</strong></td>
      <td><code>COUNTIF(inflow &gt; 0)</code> over flow rows</td>
      <td>None &mdash; a row count, not a ratio.</td>
      <td>Sum of daily values in the applied date range.</td>
    </tr>
    <tr valign="top">
      <td><strong>Players</strong></td>
      <td><code>COUNT(DISTINCT player_id)</code> with <code>inflow &gt; 0</code> for each chart point</td>
      <td>None &mdash; a distinct-player count, not a ratio.</td>
      <td>Sum of daily distinct-player counts. A player is intentionally counted again on another date.</td>
    </tr>
    <tr valign="top">
      <td><strong>Average per DEU</strong></td>
      <td><code>SUM(inflow)</code></td>
      <td>Distinct players with <code>inflow &gt; 0</code> at that chart point.</td>
      <td>Mean of daily values in the applied date range.</td>
    </tr>
    <tr valign="top">
      <td><strong>Average per DAU</strong></td>
      <td><code>SUM(inflow)</code></td>
      <td>Active players (Date); D0 cohort (Install Day); players reaching the day (Active Day).</td>
      <td>Mean of daily values in the applied date range.</td>
    </tr>
    <tr valign="top">
      <td><strong>Median / Percentile</strong></td>
      <td colspan="2"><code>APPROX_QUANTILES</code> over <code>SUM(inflow)</code> per player and date bucket. It is not a ratio. <em>Per DEU</em> keeps only players with <code>inflow &gt; 0</code>; <em>per DAU</em> uses all eligible players (D0 on Install Day; players reaching the day on Active Day), with missing flow as 0.</td>
      <td>One percentile over all player/date-bucket values in the range, not a mean of daily percentiles.</td>
    </tr>
  </tbody>
</table>

<h2>Interpretation</h2>
<ul>
  <li><strong>DEU is directional</strong>: a player who only spends contributes to outflow, not inflow. DAU is not directional: the same active-player population is used for both sides.</li>
  <li><strong>Average per DAU</strong> follows classic Resources: resource-only filters change the numerator, not the population. On cohort axes, per-DAU Average and Median / Percentile use D0 (Install Day) or players reaching the day (Active Day), with missing flow as 0. Cohort splits use their matching DAU; Group / Bucket is presentation only.</li>
  <li><strong>Percentiles</strong> are pre-aggregated per player and date bucket, so a heavy spender is not weighted by their number of flow records.</li>
</ul>

<h2>User type</h2>
<p><strong>ALL</strong> preserves existing results. <strong>HOPA</strong> and <strong>META</strong> limit flow, DEU/DAU and KPIs to matching player-days: HOPA spends energy; META opens the decoration menu. Charts are otherwise unchanged.</p>
<ul>
  <li><strong>Groups overlap.</strong> A player-day can be both types or neither, so HOPA + META is not ALL. Never add or stack them.</li>
  <li><strong>Week and month</strong> sum qualifying player-days only.</li>
  <li><strong>Before 2026-06-11</strong> flags are unknown, so typed series exclude those days.</li>
  <li><strong>META</strong> is reported from 4.0.102 and rollout-selected, so comparisons also reflect tenure.</li>
</ul>

<h2>Charts and interactions</h2>
<ul>
  <li><strong>Flow</strong> stacks inflow above zero and outflow below it. <strong>Net</strong> is inflow minus outflow; green values are non-negative and red values are negative.</li>
  <li><strong>KPIs</strong> use a separate range-wide query, independent of chart zoom, Split and Group. <strong>Affected players</strong> is 100% for per-DAU Average and Median / Percentile; otherwise it is the share of active players with flow. KPIs show no period-comparison delta, and percentile labels use the lowest selected percentile.</li>
  <li><strong>Breakdowns</strong> rank sources per side by value and share. Small sources are folded into <strong>Remaining</strong>; use its caret to reveal them. Click a row or chart legend to toggle that series, or use <strong>ALL</strong>/<strong>NONE</strong> for one side. Breakdowns follow chart zoom.</li>
  <li><strong>Group</strong> buckets a supported dimension in the chart and breakdown. Each bucket can expand to its sources; a source present in more than one bucket is split between them.</li>
</ul>

<h2>Lifetime-day axes</h2>
<p><strong>X axis</strong> switches the charts between the loaded date range and a <strong>cohort</strong> view.</p>
<ul>
  <li><strong>Install Day</strong> counts calendar days since first active day. <strong>Active Day</strong> counts only days on which a player was active (the first active day is 0).</li>
  <li><strong>The cohort</strong> is the players whose first open falls in the <strong>First Open Date</strong> range. On both lifetime axes <strong>Activity Date</strong> and app-version filters do not apply.</li>
  <li><strong>Accumulation</strong> switches between daily values and their running sum from day 0. KPI tiles retain their existing date-range calculation in this first version.</li>
  <li><strong>User type - history.</strong> These axes ignore Activity Date: ALL uses full history, while typed series start on 2026-06-11. Old cohorts can therefore show lower typed absolute values; relative modes truncate numerator and denominator together.</li>
  <li><strong>User type - normalisation.</strong> On Install Day, ALL keeps the D0 cohort while HOPA/META use active typed players on that day. Their per-DAU levels are not directly comparable. On Active Day only the type restriction changes the denominator.</li>
</ul>

<h2>Data notes</h2>
<p>Flow comes from <code>bi_viz.dashboard_resources</code>; lifetime axes use <code>install_day_no</code> or <code>active_day_no</code>, capped at day 90. Per-DAU also reads <code>dashboard_users_activity</code>. User Type filters matching daily flags on both tables; <code>NULL</code> flags are excluded.</p>

<h3>Resource Source Name - the <code>Initial*</code> values</h3>
<p>The <code>Initial*</code> sources describe how a player's starting resource state reached the data:</p>
<ul>
  <li><strong>InitialState</strong> - the real event as sent by the game.</li>
  <li><strong>InitialContentETL</strong> - synthetic row created by the ETL for players whose <code>InitialState</code> event never arrived. Mostly legacy players.</li>
  <li><strong>InitialStateDelayed</strong> - real but late <code>InitialState</code>: the player was already playing before the event shipped, so their initial state was only reported after the deployment.</li>
  <li><strong>InitialStateNullAmount</strong> - <code>InitialState</code> event that carried no amounts (empty values).</li>
</ul>
`,

    affected_players_tile: `
<p><strong>Affected players</strong> - the share of the active population that the selected metric actually covers, over the whole loaded date range.</p>
<ul>
  <li><strong>Denominator</strong> - players active at least once in the range, counted once per player (DAU source).</li>
  <li><strong>Numerator</strong> - active players with at least one inflow or outflow of the selected currency in the range. For per-DAU metrics, the statistic covers the whole active population, so the tile reads 100%.</li>
  <li><strong>Value</strong> - numerator / denominator; the caption under the value repeats the numerator as a player count.</li>
</ul>
<p>Read it as coverage of the headline value: a low share means Inflow / Outflow / Net describe a small slice of the active players. Like the other KPI tiles it comes from one range-wide query - the sidebar filters apply, while the chart's Split, Group and zoom are deliberately ignored.</p>
`,
} as const;
